import { Dispatch, SetStateAction, useCallback, useEffect } from 'react'
import usePersistentState from '../../hooks/usePersistentState'
import { Primitive } from '../../../types/global'
import { produce } from 'immer'
import setLsCodec from '../../hooks/usePersistentState/percistenceStrategies/LocalStorage/SetLsCodec'

const useTreeExpandCtrl = <T extends string | number>(
  treeKey: string,
  isOpenAll: boolean,
  groups: Record<string, any>[],
  leaf: Record<string, any>[],
  setExpandedAll: Dispatch<SetStateAction<boolean>>,
  clickedOnExpandAll: boolean,
  setClickedOnExpandAll: Dispatch<SetStateAction<boolean>>,
  debouncedSearch?: string
) => {
  const [expanded, setExpanded] = usePersistentState(new Set<T>(), treeKey + '/expanded', 'localstorage', setLsCodec)

  useEffect(() => {
    if (debouncedSearch) {
      const groupsToOpenIds: T[] = []

      groups.forEach((group) => (group.parentId ? groupsToOpenIds.push(group.parentId) : group.parentId))
      leaf.forEach((parameter) => groupsToOpenIds.push(parameter.groupId))

      setExpanded(new Set(groupsToOpenIds))
    }
  }, [debouncedSearch])

  useEffect(() => {
    if (isOpenAll && !clickedOnExpandAll) {
      const groupsToOpenIds: T[] = []

      groups.forEach((group) => (group.parentId ? groupsToOpenIds.push(group.parentId) : group.parentId))
      leaf.forEach((parameter) => groupsToOpenIds.push(parameter.groupId))

      setExpanded(new Set(groupsToOpenIds))
    }
    if (!isOpenAll && clickedOnExpandAll) {
      setExpanded(new Set())
    }
  }, [isOpenAll, clickedOnExpandAll])

  useEffect(() => {
    const groupsToOpenIds: number[] = []

    groups.forEach((group) => (group.parentId ? groupsToOpenIds.push(group.parentId) : group.parentId))
    leaf.forEach((parameter) => groupsToOpenIds.push(parameter.groupId))

    if (expanded.size === new Set(groupsToOpenIds).size) {
      setExpandedAll(true)
    } else {
      setExpandedAll(false)
    }
    setClickedOnExpandAll(false)
  }, [expanded])

  const toggleItem = useCallback((item: T) => {
    createIfNotExistsRemoveIfExists(setExpanded, item)
  }, [])

  return { toggleItem, expanded, setExpanded }
}

const createIfNotExistsRemoveIfExists = <T extends Primitive>(setter: Dispatch<SetStateAction<Set<T>>>, item: T) => {
  setter((prev) => {
    return produce(prev, (draft: Set<T>) => {
      !draft.has(item) ? draft.add(item) : draft.delete(item)
    })
  })
}

export default useTreeExpandCtrl
